import React, { useState, useEffect } from 'react';
import { Phone, Menu, X, Key } from 'lucide-react';
import { SiteData } from '../types/data';

interface HeaderProps {
  data: SiteData['header'];
}

export const Header = ({ data }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollToSection('hero')}>
          <div className="bg-primary p-2 rounded-lg">
            <Key className="w-6 h-6 text-white" />
          </div>
          <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'} md:text-2xl`}>
            {data.title}<span className="text-primary">{data.subtitle}</span>
          </span>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {data.nav.map((item) => (
            <button
              key={item}
              onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-').normalize("NFD").replace(/[\u0300-\u036f]/g, ""))}
              className={`font-medium hover:text-primary transition-colors ${
                isScrolled ? 'text-gray-700' : 'text-white/90'
              }`}
            >
              {item}
            </button>
          ))}
          <div className="flex flex-col gap-2">
            {data.phones.map((phone, index) => (
              <a 
                key={index}
                href={`tel:${phone.replace(/\s/g, '')}`} 
                className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-full font-bold transition-transform hover:scale-105"
              >
                <Phone className="w-4 h-4" />
                <span>{phone}</span>
              </a>
            ))}
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-primary"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className={`w-8 h-8 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-white shadow-lg p-4 flex flex-col gap-4 md:hidden border-t">
          {data.nav.map((item) => (
            <button
              key={item}
              onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-').normalize("NFD").replace(/[\u0300-\u036f]/g, ""))}
              className="text-left font-medium text-gray-800 py-2 border-b border-gray-100"
            >
              {item}
            </button>
          ))}
          {data.phones.map((phone, index) => (
            <a 
              key={index}
              href={`tel:${phone.replace(/\s/g, '')}`} 
              className="flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-lg font-bold mt-2"
            >
              <Phone className="w-5 h-5" />
              <span>Volat {phone}</span>
            </a>
          ))}
        </div>
      )}
    </header>
  );
};
